import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { formatMoney, orderAmount, panelRawTone, t } from '@aquabox/shared';
import { ChannelPill, ExternalStagePill, PanelStagePill } from '../../components/Pills';
import { useAbantEmployeeNames } from '../../api/queries';
import {
  resolveVehicleLabel,
  usePanelLabelLookup,
  type PanelLabelLookup,
} from '../../lib/vehicle-label';
import { itemsSummary, relativeTime } from '../../lib/labels';
import { externalStage } from '../../lib/external-stage';
import type { Order } from '../../api/types';
import { usePanelFleetSuggestions } from '../../api/panel-fleet';

type Filter = 'open' | 'all';

/**
 * Bir siparişin panel kaynağına göre durum yorumu — sınıflandırma sayım kuralıyla aynı:
 *   - panelOutcome='delivered' → done (✓)
 *   - panelOutcome='cancelled' → cancelled (iptal)
 *   - panelOutcome='pending' & tone='progress' → yolda (gösterilmeye devam eder)
 *   - panelOutcome='pending' & tone='new'      → yeni (gösterilmeye devam eder)
 *
 * "Açık" filtresinde: yeni + yolda (panel) ve bizim NEW/CONFIRMED + ASSIGNED/ON_ROUTE.
 * "Tümü" filtresinde: teslim/iptal dahil; teslim olanda ✓ rozeti.
 */
function orderBucket(o: Order): 'open' | 'closed' {
  const isPanel = o.source === 'HAYAT_PANEL' || o.source === 'ABANT_PANEL';
  if (isPanel) {
    const tone = panelRawTone(o.externalStatusRaw);
    if (tone === 'done' || tone === 'cancelled') return 'closed';
    return 'open';
  }
  if (o.status === 'DELIVERED' || o.status === 'LEFT_AT_DOOR') return 'closed';
  if (o.status === 'CANCELLED' || o.status === 'FAILED') return 'closed';
  return 'open';
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/**
 * Panel aracı sütunu: Abant'ta `useAbantEmployeeNames` ile ad, yoksa "Araç <id>".
 * Hayat'ta ham etiket (externalVehicleRaw). Bizim siparişte aracın plakası.
 *
 * Panel siparişinin `vehicleId`'i genelde null (panel kendi aracını kullanır). Bu yüzden
 * `externalVehicleRaw` + Abant çalışan eşlemesi ÖNCELİKLİ; plakaya düşmeyiz.
 *
 * Araç atanmamış panel satırında, geçmişe dayalı mahalle önerisi soluk olarak gösterilir
 * (`suggestionLabel`); amaç patrona karar desteği — siparişe YAZMAZ.
 */
function PanelVehicleCell({
  order,
  labelLookup,
  suggestionLabel,
}: {
  order: Order;
  labelLookup: PanelLabelLookup;
  suggestionLabel?: { text: string; title: string } | null;
}): ReactElement {
  const isPanel = order.source === 'HAYAT_PANEL' || order.source === 'ABANT_PANEL';
  if (!isPanel) {
    return (
      <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--primary)' }}>
        {order.vehicle?.plate ?? '—'}
      </span>
    );
  }
  const raw = (order.externalVehicleRaw ?? '').trim();
  // Çıplak numara yerine "Plaka · Personel" / "Bölge: Alt" / "Personel eşlenmemiş".
  if (raw) {
    const label = resolveVehicleLabel(order, labelLookup);
    return (
      <span
        data-testid={`live-order-vehicle-${order.id}`}
        style={{ fontWeight: 500, fontSize: 13 }}
      >
        {label.text}
      </span>
    );
  }
  return <PanelEmptyCell suggestionLabel={suggestionLabel} />;
}

/** Araç atanmamış panel hücresi + soluk mahalle önerisi satırı. */
function PanelEmptyCell({
  suggestionLabel,
}: {
  suggestionLabel?: { text: string; title: string } | null;
}): ReactElement {
  return (
    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>—</span>
      {suggestionLabel ? (
        <span
          style={{ fontSize: 11.5, color: 'var(--text-secondary)', opacity: 0.85 }}
          title={suggestionLabel.title}
          data-testid="panel-vehicle-suggestion"
        >
          {suggestionLabel.text}
        </span>
      ) : null}
    </span>
  );
}

/**
 * "Anlık gelen siparişler" kartı (kullanıcı kararı 30.09.2026).
 *
 * Bugünün tüm siparişleri (panel + bizim), en yeni üstte. Varsayılan filtre "Açık":
 * yeni + yolda. "Tümü" sekmesinde teslim/iptal de görünür; teslim olanda ✓ rozeti.
 *
 * Panel siparişinde "bölge eşleşmedi" GÖSTERİLMEZ — panel kendi aracını yönetir,
 * bölge kuralı orada işlemez (kullanıcı kararı 30.09.2026).
 */
export function LiveOrdersCard({ orders }: { orders: Order[] }) {
  const [filter, setFilter] = useState<Filter>('open');
  const employeeNames = useAbantEmployeeNames();
  const labelLookup = usePanelLabelLookup();
  // Mahalle → araç öneri sorgusu (60 sn yenileme). Defter değişmez; yalnız bilgi.
  const suggestionsQ = usePanelFleetSuggestions();
  // orderId → görünen metin + title; araç atanmamış panel satırlarında gösterilir.
  const suggestionByOrderId = useMemo(() => {
    const out = new Map<string, { text: string; title: string }>();
    const list = suggestionsQ.data?.openOrders ?? [];
    for (const o of list) {
      if (!o.suggestion) continue;
      const label = o.suggestion.vehicleLabel;
      // Abant etiketi çalışan kimliğidir; hücrenin kendisiyle aynı adlandırma kullanılır.
      const name =
        o.source === 'ABANT_PANEL'
          ? (employeeNames[label] ?? t('orders.vehicleLabel.unmapped'))
          : label;
      out.set(o.orderId, {
        text: t('panelFleet.suggest.label', { name }),
        title: t('panelFleet.suggest.shareSummary', {
          days: suggestionsQ.data?.days ?? 30,
          total: o.suggestion.total,
          pct: o.suggestion.sharePct,
        }),
      });
    }
    return out;
  }, [suggestionsQ.data, employeeNames]);

  const filtered = useMemo(() => {
    return orders
      .filter((o) => (filter === 'open' ? orderBucket(o) === 'open' : true))
      .sort((a, b) => {
        // ON_ROUTE_LONG en üste
        const stageA = externalStage({
          source: a.source,
          status: a.status,
          externalStatusRaw: a.externalStatusRaw,
          receivedAt: a.receivedAt,
        });
        const stageB = externalStage({
          source: b.source,
          status: b.status,
          externalStatusRaw: b.externalStatusRaw,
          receivedAt: b.receivedAt,
        });
        if (stageA === 'ON_ROUTE_LONG' && stageB !== 'ON_ROUTE_LONG') return -1;
        if (stageB === 'ON_ROUTE_LONG' && stageA !== 'ON_ROUTE_LONG') return 1;
        return new Date(b.receivedAt).getTime() - new Date(a.receivedAt).getTime();
      });
  }, [orders, filter]);

  return (
    <section className="card card-pad">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: 'var(--success)' }} />
        <h2 className="section-title">{t('panel.liveOrders')}</h2>
        <div
          style={{
            display: 'flex',
            padding: 3,
            background: 'var(--page-bg)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-pill)',
            marginLeft: 'auto',
          }}
        >
          {(
            [
              { key: 'open', label: t('panel.liveFilterOpen') },
              { key: 'all', label: t('panel.liveFilterAll') },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              style={{
                padding: '6px 12px',
                fontSize: 12.5,
                fontWeight: 600,
                borderRadius: 'var(--r-pill)',
                background: filter === tab.key ? 'var(--primary)' : 'transparent',
                color: filter === tab.key ? '#fff' : 'var(--text-secondary)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <p className="muted-line" style={{ marginBottom: 14 }}>
        {t('panel.liveOrdersSub')}
      </p>

      {filtered.length === 0 ? (
        <div
          style={{
            padding: '20px 0',
            textAlign: 'center',
            color: 'var(--text-secondary)',
            fontSize: 13.5,
          }}
        >
          {t('panel.liveEmpty')}
        </div>
      ) : (
        <ul style={{ listStyle: 'none' }}>
          {filtered.map((o) => {
            const isPanel = o.source === 'HAYAT_PANEL' || o.source === 'ABANT_PANEL';
            const extStage = isPanel
              ? externalStage({
                  source: o.source,
                  status: o.status,
                  externalStatusRaw: o.externalStatusRaw,
                  receivedAt: o.receivedAt,
                })
              : null;
            const isDone =
              extStage === 'DELIVERED' ||
              (isPanel && panelRawTone(o.externalStatusRaw) === 'done') ||
              (!isPanel && (o.status === 'DELIVERED' || o.status === 'LEFT_AT_DOOR'));
            return (
              <li
                key={o.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '52px 180px minmax(0, 1fr) minmax(0, 1fr) 200px 120px 76px',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 4px',
                  borderTop: '1px solid var(--border)',
                  fontSize: 13,
                }}
              >
                <span style={{ fontWeight: 600, fontSize: 12.5 }}>{timeLabel(o.receivedAt)}</span>
                <ChannelPill channel={o.channel} source={o.source} />
                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 13.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={o.customerName}
                >
                  {o.customerName}
                </span>
                <span
                  style={{
                    fontSize: 12.5,
                    color: 'var(--text-secondary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={o.address}
                >
                  {o.address}
                </span>
                {isPanel ? (
                  <span style={{ minWidth: 0, overflow: 'hidden' }}>
                    {extStage ? (
                      <ExternalStagePill
                        source={o.source}
                        status={o.status}
                        externalStatusRaw={o.externalStatusRaw}
                        receivedAt={o.receivedAt}
                      />
                    ) : (
                      <PanelStagePill source={o.source} raw={o.externalStatusRaw} />
                    )}
                  </span>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {itemsSummary(o.items)}
                  </span>
                )}
                <PanelVehicleCell
                  order={o}
                  labelLookup={labelLookup}
                  suggestionLabel={
                    (o.source === 'HAYAT_PANEL' || o.source === 'ABANT_PANEL') &&
                    !(o.externalVehicleRaw ?? '').trim()
                      ? (suggestionByOrderId.get(o.id) ?? null)
                      : undefined
                  }
                />
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    textAlign: 'right',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'flex-end',
                    fontWeight: 600,
                  }}
                  title={formatMoney(orderAmount(o))}
                >
                  {isDone ? <span style={{ color: 'var(--success)' }}>✓</span> : null}
                  {relativeTime(o.receivedAt)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {orders.length === 0 ? null : (
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <Link
            to="/siparisler"
            style={{ color: 'var(--primary)', fontSize: 12.5, fontWeight: 600 }}
          >
            {t('panel.goToOrders')} →
          </Link>
        </div>
      )}
    </section>
  );
}
